// React import
import { useState, useRef, useEffect, useMemo } from 'react';

// Generic imports
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { useDrop } from 'react-dnd';
import CurrencyList from 'currency-list';
import pluralize from 'pluralize';

// Mui imports
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import Drawer from '@mui/material/Drawer';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import { grey, red } from '@mui/material/colors';
import { useTheme, alpha } from '@mui/material/styles';

// FontAwesomeIcon imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEdit, faTrash, faPlus, faXmark, faClock, faCalendar, faRoute, faBuilding, faShuffle, faCopy,
    faFileCirclePlus, faInfoCircle, faCheckCircle, faBarcodeRead, faEye, faEyeSlash,
    faCircleDollarToSlot} from '@fortawesome/pro-light-svg-icons';

// Local imports
import OpportunityOfferFieldRow from './OpportunityOneOffBuilder.fieldrow.js';
import SectionHeader from './OpportunityOneOffBuilder.sectionheader.js';
import SectionOfferHeader from './OpportunityOneOffBuilder.sectionofferheader.js';
import AddTemplate from './OpportunityOneOffBuilder.addtemplate.js';
import RatesDetails from '../../RatesDetails.js';
import { RateCard } from '../../RateCard.js';
import TitleableReorders from '../components/TitleableReorders.js';
import ContextMenu from '../components/ContextMenu.js';
import OpportunityTitleEditModal from './OpportunityTitleEditModal.js';
import OpportunityCargowiseCodesModal from './OpportunityCargowiseCodesModal.js';

import SpecialInputField from './SpecialInputField.js';
import { findMatchingProducts } from '../utils/product.js';
import { DisplayCurrencySettingsOffer, DisplayCurrencySettingsSection, DisplayCurrencySettingsField, BulkUpdateSettings } from './DisplayCurrencySettings.js';
import ProfitWidget from '../components/ProfitWidget.js';
import TermsDialog from './OpportunityOneOffBuilder.terms.js';
import { FieldTypes } from '../../FieldTypes.js';
import { convertRateKgToLbsIfNeeded } from '../utils/rates.js';


// A rate dropped onto the quote is kept as a copy on the opportunity, and the charge lines built
// from it point into that copy by field id. Dropping a rate that is already on the quote therefore
// has to take the copy just dropped: _.uniqBy keeps the first entry it sees, so listing the
// existing rates first quietly kept the older snapshot while the new charge lines referenced
// fields only the newer one has. getFieldRatesForProduct finds no field for those ids and returns
// nothing, so the charge appears on the quote and prices at nothing — which is what a supplier
// adding a fuel or a fee to a rate that was already on a quote looked like.
//
// Replaced where it stands rather than moved to the front, so the costs do not reorder themselves
// under whoever is reading them.
function withRate(rates, rate) {
    const existing = rates || [];
    return existing.some(br => br.id === rate.id)
        ? existing.map(br => (br.id === rate.id ? rate : br))
        : [...existing, rate];
}

function OpportunityOneOffBuilder(props) {
    const { 
        offers, setOffers, routes, products, org, minimizedSearch, seaPorts, unlocode, 
        transportationMethod, status, onCreateTemplate, owner, me, errors, buyingRates, setBuyingRates, 
        suppliers, accesstoken, exchangeRates, contracts, account, lockedExchangeRates, terms, setTerms, 
        forceOpen, setForceOpen, opportunity, onUpdateOpportunity, opportunityId, onPreview
    } = props;
    const [ modal, setModal ] = useState({ open: false });
    const [ sortModal, setSortModal ] = useState({ open: false });
    const [ showInformationBar, setShowInformationBar ] = useState([]);
    const [ ratesMoreInfo, setRatesMoreInfo ] = useState({ open: false });
    const [ cargowiseCodes, setCargowiseCodes ] = useState(null);

    const [ showAddGroupAnimation, setShowAddGroupAnimation ] = useState(false);
    const [ addBuyingRateDialog, setAddBuyingRateDialog ] = useState(null);
    const [ offerDisplayCurrency, setOfferDisplayCurrency ] = useState(null);
    const [ termsOpen, setTermsOpen ] = useState(false);
    const [ editingOfferTitle, setEditingOfferTitle ] = useState(null);
    const [ editingOfferTitleValue, setEditingOfferTitleValue ] = useState('');
    const [ editingOfferDescription, setEditingOfferDescription ] = useState(null);
    const [ editingOfferDescriptionValue, setEditingOfferDescriptionValue ] = useState('');
    const setShowAddGroupAnimationRef = useRef();
    setShowAddGroupAnimationRef.current = setShowAddGroupAnimation;
    const theme = useTheme();

    // Auto-populate default terms when opportunity is first loaded
    const hasAutoPopulatedRef = useRef(false);
    const lastTransportationMethodRef = useRef(transportationMethod);
    
    useEffect(() => {
        // Reset the ref if transportation method changed from undefined/null to a value
        // This allows auto-population to happen when transportation method is set later
        if (!lastTransportationMethodRef.current && transportationMethod) {
            hasAutoPopulatedRef.current = false;
        }
        lastTransportationMethodRef.current = transportationMethod;
    }, [transportationMethod]);
    
    useEffect(() => {
        // Only auto-populate once per opportunity load (unless transportation method was just set)
        if (hasAutoPopulatedRef.current) return;
        
        // Auto-populate if terms is null/undefined or empty array
        // This will happen for new opportunities that haven't had terms set yet
        const hasNoTerms = terms === null || terms === undefined || (Array.isArray(terms) && terms.length === 0);
        
        if (!hasNoTerms) {
            hasAutoPopulatedRef.current = true; // Mark as processed even if we don't auto-populate
            return;
        }
        if (!transportationMethod) return; // Need transportation method to know which terms to use
        if (!org?.terms) return;
        
        // Auto-populate default terms based on transportation method
        let newTerms = null;
        if (org.terms[`price-quote-${transportationMethod}`]) {
            newTerms = [{ name: `price-quote-${transportationMethod}`, value: org.terms[`price-quote-${transportationMethod}`] }];
        } else if (org.terms['price-quote']) {
            newTerms = [{ name: 'price-quote', value: org.terms['price-quote'] }];
        }
        
        if (newTerms) {
            hasAutoPopulatedRef.current = true;
            setTerms(newTerms);
        } else {
            hasAutoPopulatedRef.current = true; // Mark as processed even if no terms found
        }
    }, [terms, transportationMethod, org?.terms, setTerms]);
    
    // Reset the ref when opportunity changes
    useEffect(() => {
        hasAutoPopulatedRef.current = false;
        lastTransportationMethodRef.current = transportationMethod;
    }, [opportunityId, transportationMethod]);

    useEffect(() => {
        const ids = _(offers).map(offer => offer.sections).flatten().map(section => section.offers).flatten().map(choice => {
            if (choice.transitTime) return choice.id;
            if (choice.validUntil || choice.validFrom) return choice.id;
            if (choice.rates) return choice.id;
            if (choice.carrier) return choice.id;
            return null;
        }).flatten().compact().value()
        setShowInformationBar(ids);

        // Little migration
        offers.map(o => delete o.buyingRates);
    }, []);

    useEffect(() => {
        let changed = false;
        // Clean up fields buying rates
        offers?.forEach(o => o.sections.forEach(s => s.offers.forEach(o => o.fields.forEach(f => {
            const a = _.cloneDeep(f.buyingRates);
            f.buyingRates = (f.buyingRates || []).filter(br => buyingRates.find(br2 => br2.id === br.rateId));
            const b = _.cloneDeep(f.buyingRates);
            if (!_.isEqual(a, b)) {
                changed = true;
            }
        }))));
        if (changed) {
            setOffers(_.cloneDeep(offers));
        }
    }, [offers]);

    useEffect(() => {
        if (forceOpen?.match(/builder:\d+:offertitle/)) {
            const offerIndex = forceOpen.match(/builder:(\d+):offertitle/)[1];
            setModal({
                title: 'Set Offer\'s Title',
                label: 'Title',
                value: offers[offerIndex].title,
                path: `[${offerIndex}].title`,
                open: true,
                suggestions: [
                    ...(routes || []).map(route => `${route.source?.text || 'Everywhere'} to ${route.destination?.text || 'Everywhere'}`),
                    ..._.uniq(getRatesForOffer({ buyingRates, offer: offers[offerIndex] }).map(rate => rate.supplier.organization)),
                ]
            });
            setForceOpen(null);
        }
    }, [forceOpen]);

    const locked = (status && status !== 'draft') || owner !== me.email;

    function onShowRates({ fieldBuyingRates, fieldId, supplier }) {
        const ratesToShow = buyingRates.filter(f => fieldBuyingRates.find(ff => ff.rateId === f.id) && f.supplier.organization === supplier);
        setRatesMoreInfo({ open: true, rates: ratesToShow, emphasize: fieldBuyingRates, fieldId });
    }

    function onAddBuyingRate({ rate, rateFieldId, opportunityFieldId, force = false }) {
        return new Promise(_resolve => {
            // Convert rate from KG to LBS if needed
            const convertedRate = convertRateKgToLbsIfNeeded({ rate, opportunityProducts: products });

            if (convertedRate.type !== 'tariff') {
                setBuyingRates(_.uniqBy([convertedRate, ...buyingRates], br => br.id));
            }

            const { path: ratePath } = findPathForField({ offers: [convertedRate.offer], fieldId: rateFieldId });
            const rateField = _.get([convertedRate.offer], ratePath)
            const { path } = findPathForField({ offers, fieldId: opportunityFieldId });
            const opportunityField = _.get(offers, path)

            if (rateField.type === 'flat' && opportunityField.type !== 'flat' && !force) {
                setAddBuyingRateDialog({ open: true, rate: convertedRate, rateFieldId, opportunityFieldId, resolve: _resolve });
                return;
            }

            setOffers(_offers => {
                const newOffers = _.cloneDeep(_offers);
                if (convertedRate.type !== 'tariff') {
                    const _fieldBuyingRates = _.get(newOffers, `${path}.buyingRates`) || [];
                    const _products = findMatchingProducts({ products, product: convertedRate.product });

                    if (rateField.type === 'flat') {
                        _products.splice(1)
                    }

                    _products.forEach(product => {
                        _fieldBuyingRates.push({ rateId: convertedRate.id, fieldId: rateFieldId, opportunityProductId: product?.id });
                    });
                    _.set(newOffers, `${path}.buyingRates`, _.uniqWith(_fieldBuyingRates, _.isEqual));
                } else {
                    products.forEach(product => {
                        if (product.type === convertedRate.product.type) {
                            _.set(newOffers, `${path}.values[${product.id}]`, _.get([convertedRate.offer], `${ratePath}.values[${convertedRate.product.id}]`));
                        }
                    });
                    if (!_.get(newOffers, `${path}.title`)) {
                        _.set(newOffers, `${path}.title`, _.get([convertedRate.offer], `${ratePath}.title`));
                    }
                    if (_.get([convertedRate.offer], `${ratePath}.type`) !== 'flat' && _.get(newOffers, `${path}.type`) !== 'per-unit-type') {
                        _.set(newOffers, `${path}.type`, 'per-unit-type');
                    }
                }
                setTimeout(() => {
                    _resolve();
                });
                return newOffers;
            })
        });
    }

    function onCargowiseCodes({ offer }) {
        setCargowiseCodes({ offer });
    }

    if (!locked && (!transportationMethod || _.isEmpty(products))) {
        return (
            <Box sx={{ background: theme.palette.background.default, paddingBottom: '24px' }}>
                <Box sx={{ background: 'transparent', padding: '6px 24px', mb: 3, borderBottom: '1px solid rgba(59, 130, 246, 0.3)', boxShadow: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant='body1' sx={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#334155' }}>
                        Quote Builder
                    </Typography>
                    {onPreview && (
                        <Button variant='text' color='primary' sx={{ textTransform: 'none', fontSize: '12px', padding: '4px 8px' }} onClick={onPreview}>Preview</Button>
                    )}
                </Box>

                <Box sx={{ ml: 3, mr: 3, mt: 1, mb: 1 }}>
                    <Alert severity='info'>
                        Please select a transportaion method, and add some products, to start building your quote.
                    </Alert>
                </Box>
            </Box>
        )
    }

    return (
        <Box sx={{ background: theme.palette.background.default, paddingBottom: '24px' }}>

            <Box sx={{ background: 'transparent', padding: '6px 24px', mb: 3, borderBottom: '1px solid rgba(59, 130, 246, 0.3)', boxShadow: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant='body1' sx={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#334155' }}>
                    Quote Builder
                </Typography>
                {onPreview && (
                    <Button variant='text' color='primary' sx={{ textTransform: 'none', fontSize: '12px', padding: '4px 8px' }} onClick={onPreview}>Preview</Button>
                )}
            </Box>

            {!_.isEmpty(errors) && (
                <Alert severity='error' sx={{ marginBottom: '48px' }} fullWidth>
                    You have {errors.length === 1 ? 'an error' : 'some errors'} in the form.
                </Alert>
            )}
            {offers.map((offer, offerIndex) => (
                <Paper sx={{ padding: '16px', background: theme.palette.panel1, border:`1px solid ${theme.palette.border1}`, marginBottom: '16px', marginLeft: '24px', marginRight: '24px', borderRadius: '6px' }} elevation={0} key={offer.id}>

                    <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                        {editingOfferTitle === offerIndex && !locked ? (
                            <TextField
                                autoFocus
                                value={editingOfferTitleValue}
                                onChange={(e) => setEditingOfferTitleValue(e.target.value)}
                                onBlur={() => {
                                    const newOffers = _.cloneDeep(offers);
                                    _.set(newOffers, `[${offerIndex}].title`, editingOfferTitleValue);
                                    setOffers(newOffers);
                                    setEditingOfferTitle(null);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.target.blur();
                                    } else if (e.key === 'Escape') {
                                        setEditingOfferTitleValue(offer.title || '');
                                        setEditingOfferTitle(null);
                                    }
                                }}
                                variant='standard'
                                sx={{ flex: 0, minWidth: '200px', marginRight: '8px' }}
                            />
                        ) : (
                            <>
                                <Box 
                                    sx={{ 
                                        fontWeight: 600, 
                                        fontSize: '18px', 
                                        color: theme.palette.text.primary,
                                        cursor: locked ? 'default' : 'pointer',
                                        '&:hover': locked ? {} : { textDecoration: 'underline' }
                                    }}
                                onClick={() => {
                                        if (!locked) {
                                            setEditingOfferTitleValue(offer.title || '');
                                            setEditingOfferTitle(offerIndex);
                                        }
                                    }}
                                >
                                    {offer.title || <i style={{ opacity: 0.5 }}>New Offer</i>}
                                </Box>
                            </>
                        )}
                        <Box sx={{ marginLeft: '8px', display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ margin: '0px 8px 0px 0px' }}>
                                <SpecialInputField
                                    icon={<FontAwesomeIcon icon={faClock} />}
                                    emptyStateText={'Transit Time'}
                                    title={'Set Transit Time'}
                                    label={'Transit Time'}
                                    path={`[${offerIndex}].transitTime`}
                                    options={getRatesForOffer({ buyingRates, offer }).map(r => r.transportationMethod === 'sea' ? r.offer.transitTime * 24 : r.offer.transitTime)}
                                    optionsDescription={'Add a transit time to the entire offer. Cost rates transit times:'}
                                    modal={modal}
                                    setModal={setModal}
                                    offers={offers}
                                    setOffers={setOffers}
                                    disabled={(!!status && status !== 'draft') || locked}
                                    inputType='transit-time'
                                />
                            </Box>

                            <Box sx={{ margin: '0px 8px 0px 0px' }}>
                                <SpecialInputField
                                    icon={<FontAwesomeIcon icon={faCalendar} />}
                                    emptyStateText={'Validity'}
                                    title={'Set Validity'}
                                    label={'Validity'}
                                    path={`[${offerIndex}]`}
                                    inputType='validity-range'
                                    options={getRatesForOffer({ buyingRates, offer }).map(r => r.offer.validUntil)}
                                    optionsDescription={'Let your customers know from and until what date the offer is valid. Cost rates validity:'}
                                    modal={modal}
                                    setModal={setModal}
                                    offers={offers}
                                    setOffers={setOffers}
                                    disabled={(!!status && status !== 'draft') || locked}
                                />
                            </Box>

                            <Box sx={{ margin: '0px 8px 0px 0px' }}>
                                <SpecialInputField
                                    icon={<FontAwesomeIcon icon={faRoute} />}
                                    emptyStateText={'Route'}
                                    title={'Set Route'}
                                    label={'Route'}
                                    path={`[${offerIndex}].routes`}
                                    inputType='routes'
                                    modal={modal}
                                    setModal={setModal}
                                    offers={offers}
                                    setOffers={setOffers}
                                    disabled={(!!status && status !== 'draft') || locked}
                                />
                            </Box>

                            <Box sx={{ margin: '0px 8px 0px 0px' }}>
                                <SpecialInputField
                                    icon={<FontAwesomeIcon icon={faBuilding} />}
                                    emptyStateText={'Carrier'}
                                    title={'Set Carrier'}
                                    label={'Carrier'}
                                    path={`[${offerIndex}].carrier`}
                                    inputType='carrier'
                                    optionsDescription={'Add a carrier to the entire offer.'}
                                    modal={modal}
                                    setModal={setModal}
                                    offers={offers}
                                    setOffers={setOffers}
                                    disabled={(!!status && status !== 'draft') || locked}
                                />
                            </Box>

                            <Box sx={{ margin: '0px 8px 0px 0px' }}>
                                <Tooltip title={
                                    (offer.displayCurrency?.convert || offer.displayCurrency?.length === 3)
                                        ? CurrencyList.getAll('en_US')[offer.displayCurrency?.to || offer.displayCurrency].name
                                        : 'Display currency'
                                }>
                                    <span>
                                        <Button
                                            variant='text'
                                            sx={{
                                                minWidth: '32px',
                                                width: '32px',
                                                height: '32px',
                                                padding: '0px',
                                                borderRadius: '6px',
                                                border: 'none',
                                                color: (offer.displayCurrency?.convert || offer.displayCurrency?.length === 3) ? theme.palette.primary.main : theme.palette.text.secondary,
                                                '&:hover': {
                                                    backgroundColor: (offer.displayCurrency?.convert || offer.displayCurrency?.length === 3) ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.text.secondary, 0.08),
                                                    border: 'none',
                                                    color: (offer.displayCurrency?.convert || offer.displayCurrency?.length === 3) ? theme.palette.primary.main : theme.palette.text.secondary
                                                },
                                                '&.Mui-disabled': {
                                                    color: theme.palette.action.disabled,
                                                    border: 'none'
                                                },
                                                '&:focus': {
                                                    outline: 'none',
                                                    border: 'none'
                                                }
                                            }}
                                            onClick={() => setOfferDisplayCurrency({ offer })}
                                        >
                                            <FontAwesomeIcon icon={faCircleDollarToSlot} style={{ fontSize: '13px' }} />
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Box>
                        </Box>
                        <Box sx={{ flex: 1 }} />
                        {(!status || status === 'draft') && !locked && (
                            <ContextMenu
                                options={[
                                    { 
                                        key: 1, 
                                        label: 'Reorder sections', 
                                        onClick: () => setSortModal({ open: true, title: 'Sort Sections', items: offer.sections, path: `[${offerIndex}].sections` }),
                                        icon: <FontAwesomeIcon icon={faShuffle} /> 
                                    },
                                    { 
                                        key: 'divider-0',
                                    },
                                    { 
                                        key: 2,
                                        label: 'Add offer',
                                        icon: <FontAwesomeIcon icon={faPlus} />,
                                        onClick: () => setTimeout(() => {
                                            window.scrollTo(0, document.body.scrollHeight);
                                            setShowAddGroupAnimationRef.current(true);
                                            setTimeout(() => {
                                                setShowAddGroupAnimationRef.current(false);
                                            }, 5000);
                                        }, 100)
                                    },
                                    { 
                                        key: 3,
                                        label: 'Delete offer',
                                        icon: <FontAwesomeIcon icon={faTrash} />,
                                        onClick: () => setOffers(offers.filter(o => o.id !== offer.id))
                                    },
                                    {
                                        key: 7,
                                        label: 'Duplicate offer...',
                                        icon: <FontAwesomeIcon icon={faCopy} />,
                                        submenu: [{
                                            key: '7.1',
                                            label: 'Duplicate offer with buying rates',
                                            icon: <FontAwesomeIcon icon={faCopy} />,
                                            onClick: () => {
                                                const newIds = {};
                                                const newOffer = _.cloneDeep(offer);
                                                newOffer.title = `[Duplicated] ${offer.title}`;
                                                newIds[offer.id] = uuidv4();
                                                newOffer.id = newIds[offer.id];
                                                newOffer.sections.forEach(section => {
                                                    newIds[section.id] = uuidv4();
                                                    section.id = newIds[section.id];
                                                    section.offers.forEach(offer => {
                                                        newIds[offer.id] = uuidv4();
                                                        offer.id = newIds[offer.id];
                                                        offer.fields.forEach(field => {
                                                            newIds[field.id] = uuidv4();
                                                            field.id = newIds[field.id];
                                                        });
                                                    });
                                                });
                                                setOffers([...offers, newOffer])
                                            }
                                        }, {
                                            key: '7.2',
                                            label: 'Duplicate offer without buying rates',
                                            icon: <FontAwesomeIcon icon={faCopy} />,
                                            onClick: () => {
                                                const newIds = {};
                                                const newOffer = _.cloneDeep(offer);
                                                newOffer.title = `[Duplicated] ${offer.title}`;
                                                newIds[offer.id] = uuidv4();
                                                newOffer.id = newIds[offer.id];
                                                newOffer.sections.forEach(section => {
                                                    newIds[section.id] = uuidv4();
                                                    section.id = newIds[section.id];
                                                    section.offers.forEach(offer => {
                                                        newIds[offer.id] = uuidv4();
                                                        offer.id = newIds[offer.id];
                                                        offer.fields.forEach(field => {
                                                            newIds[field.id] = uuidv4();
                                                            field.id = newIds[field.id];
                                                            field.buyingRates = [];
                                                        });
                                                    });
                                                });
                                                setOffers([...offers, newOffer])
                                            }
                                        }]
                                    },
                                    { 
                                        key: 'divider-1',
                                    },
                                    { 
                                        key: 4,
                                        label: 'Create template from offer',
                                        icon: <FontAwesomeIcon icon={faFileCirclePlus} />,
                                        onClick: () => onCreateTemplate({ offer })
                                    },
                                    { 
                                        key: 5, 
                                        label: 'Set up Cargowise codes', 
                                        onClick: () => onCargowiseCodes({ offer }),
                                        icon: <FontAwesomeIcon icon={faBarcodeRead} /> 
                                    },
                                ]}
                            />
                        )}
                        {((status && status !== 'draft') || locked) && (
                            <ContextMenu
                                options={[
                                    {
                                        key: 1,
                                        label: 'Set up Cargowise codes',
                                        onClick: () => onCargowiseCodes({ offer }),
                                        icon: <FontAwesomeIcon icon={faBarcodeRead} />
                                    },
                                ]}
                            />
                        )}
                    </Box>
                    {errors.find(a => a.id === `${offerIndex}:offertitle`) && (
                        <Alert sx={{ mt: 1, mb: 1 }} severity={'error'}>
                            Insert a name for the offer
                        </Alert>
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', paddingRight: '28px', marginTop: '4px', marginBottom: '12px' }}>
                        {editingOfferDescription === offerIndex && !locked ? (
                            <TextField
                                autoFocus
                                value={editingOfferDescriptionValue}
                                onChange={(e) => setEditingOfferDescriptionValue(e.target.value)}
                                onBlur={() => {
                                    const newOffers = _.cloneDeep(offers);
                                    _.set(newOffers, `[${offerIndex}].description`, editingOfferDescriptionValue);
                                    setOffers(newOffers);
                                    setEditingOfferDescription(null);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.target.blur();
                                    } else if (e.key === 'Escape') {
                                        setEditingOfferDescriptionValue(offer.description || '');
                                        setEditingOfferDescription(null);
                                    }
                                }}
                                variant='standard'
                                placeholder='You can add a description to the offer here.'
                                sx={{ flex: 1, minWidth: '200px' }}
                            />
                        ) : (
                            <Box
                                sx={{
                                    fontWeight: 400,
                                    fontSize: '13px',
                                    color: theme.palette.text.secondary,
                                    cursor: locked ? 'default' : 'pointer',
                                    '&:hover': locked ? {} : { textDecoration: 'underline' }
                                }}
                                onClick={() => {
                                    if (!locked) {
                                        setEditingOfferDescriptionValue(offer.description || '');
                                        setEditingOfferDescription(offerIndex);
                                    }
                                }}
                            >
                                {offer.description || <i style={{ opacity: 0.6 }}>You can add a description to the offer here.</i>}
                    </Box>
                        )}
                    </Box>

                    {offer.sections.map((section, sectionIndex) => (
                        <Box sx={{ display: 'flex', flexDirection: 'column', marginTop: '20px' }} key={section.id}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start', justifyContent: 'center', padding: '8px 0px 0px 0px' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                                    {(!status || status === 'draft') && !locked && (
                                        <ContextMenu
                                            options={_.compact([
                                                { 
                                                    key: 1, 
                                                    label: section.offers.length > 1 ? 'Reorder choices' : 'Reorder fields', 
                                                    icon: <FontAwesomeIcon icon={faShuffle} />,
                                                    onClick: () => {
                                                        if (section.offers.length > 1) {
                                                            setSortModal({ open: true, title: 'Sort Choices', items: section.offers, path: `[${offerIndex}].sections[${sectionIndex}].offers` })
                                                        } else {
                                                            setSortModal({ open: true, title: 'Sort Fields', items: section.offers[0].fields, path: `[${offerIndex}].sections[${sectionIndex}].offers[0].fields` });
                                                        }
                                                    },
                                                    disabled: section.offers.length === 0,
                                                },
                                                (section.offers.length === 1 ? {
                                                    key: 2,
                                                    label: 'Add information bar',
                                                    icon: <FontAwesomeIcon icon={faInfoCircle} />,
                                                    onClick: () => {
                                                        setShowInformationBar(ib => ([...ib, section.offers[0].id]));
                                                    }

                                                } : null),
                                                (section.offers.length <= 1 ? {
                                                    key: 3,
                                                    label: 'Offer choices for this section',
                                                    icon: <FontAwesomeIcon icon={faCheckCircle} />,
                                                    onClick: () => {
                                                        const newOffers = _.cloneDeep(offers);
                                                        const newSectionOffers = [
                                                            ...section.offers.map(o => ({...o, title: o.title || 'Choice 1'})), 
                                                            { id: uuidv4(), title: 'Choice 2', fields: section.offers[0].fields.map(f => ({...f, id: uuidv4(), buyingRates: [], values: _.mapValues(v => ({...v, value: 0})) })) }
                                                        ];
                                                        _.set(newOffers, `[${offerIndex}].sections[${sectionIndex}].offers`, newSectionOffers);
                                                        setOffers(newOffers);
                                                        setShowInformationBar(b => _.uniq([...b, ...newSectionOffers.map(a => a.id)]))
                                                    }
                                                } : null),
                                                {
                                                    key: 5,
                                                    label: section.hideBreakdown ? 'Show breakdown for section' : 'Don\'t show breakdown for section',
                                                    icon: <FontAwesomeIcon icon={section.hideBreakdown ? faEye : faEyeSlash} />,
                                                    onClick: () => {
                                                        const newOffers = _.cloneDeep(offers);
                                                        newOffers[offerIndex].sections[sectionIndex].hideBreakdown = !section.hideBreakdown;
                                                        setOffers(newOffers);
                                                    }
                                                },
                                                { 
                                                    key: 6,
                                                    label: 'Delete section',
                                                    icon: <FontAwesomeIcon icon={faTrash} />,
                                                    onClick: () => {
                                                        const sections = offer.sections.filter(s => s.id !== section.id);
                                                        const newOffers = _.cloneDeep(offers);
                                                        newOffers[offerIndex].sections = sections;
                                                        setOffers(newOffers);
                                                    }
                                                }
                                            ])}
                                        />
                                    )}
                                </Box>
                                <SectionHeader 
                                    offer={offer}
                                    section={section}
                                    offerIndex={offerIndex}
                                    sectionIndex={sectionIndex} 
                                    locked={locked}
                                    setModal={setModal}
                                    modal={modal}
                                    products={products}
                                    exchangeRates={exchangeRates}
                                    buyingRates={buyingRates}
                                    transportationMethod={transportationMethod}
                                    offers={offers}
                                    setOffers={setOffers}
                                    status={status}
                                    setOfferDisplayCurrency={setOfferDisplayCurrency}
                                    organization={org}
                                    onAddSection={({ rate, section: updateSection }) => {
                                        const newOffers = _.cloneDeep(offers);
                                        const newSection = newOffers[offerIndex].sections[sectionIndex];
                                        updateSectionFromRateOrTariff({ rate, section: newSection, updateSection, products, organization: org });
                                        if (rate.type !== 'tariff') setBuyingRates(withRate(buyingRates, rate));
                                        setOffers(newOffers);
                                    }}
                                />
                                {section.offers.length === 1 && (
                                    <Box sx={{ margin: '4px 0px', display: 'flex', alignItems: 'center' }}>
                                        <Box sx={{ margin: '0px 6px 0px 0px' }}>
                                            <ProfitWidget
                                                text={'Section'}
                                                section={section}
                                                sectionOffer={section.offers[0]}
                                                sections={offer.sections}
                                                products={products}
                                                exchangeRates={exchangeRates}
                                                buyingRates={buyingRates}
                                                transportationMethod={transportationMethod}
                                                lockedExchangeRates={lockedExchangeRates}
                                                organization={org}
                                            />
                                        </Box>
                                    </Box>
                                )}
                                {section.offers.length > 1 && (
                                    <Box sx={{ margin: '12px', display: 'flex', alignItems: 'center' }}>
                                        <Box sx={{ margin: '0px 8px 0px 0px' }}>
                                            <Tooltip title={
                                                (section.displayCurrency?.convert || section.displayCurrency?.length === 3)
                                                    ? CurrencyList.getAll('en_US')[section.displayCurrency?.to || section.displayCurrency].name
                                                    : 'Display currency'
                                            }>
                                                <span>
                                            <Button
                                                variant='text'
                                                        sx={{
                                                            minWidth: '32px',
                                                            width: '32px',
                                                            height: '32px',
                                                            padding: '0px',
                                                            borderRadius: '6px',
                                                            color: (section.displayCurrency?.convert || section.displayCurrency?.length === 3) ? theme.palette.primary.main : theme.palette.text.secondary,
                                                            '&:hover': {
                                                                backgroundColor: (section.displayCurrency?.convert || section.displayCurrency?.length === 3) ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.text.secondary, 0.08),
                                                                color: (section.displayCurrency?.convert || section.displayCurrency?.length === 3) ? theme.palette.primary.main : theme.palette.text.secondary
                                                            }
                                                        }}
                                                onClick={() => setOfferDisplayCurrency({ offer, section })}
                                            >
                                                        <FontAwesomeIcon icon={faCircleDollarToSlot} style={{ fontSize: '13px' }} />
                                            </Button>
                                                </span>
                                            </Tooltip>
                                        </Box>
                                        <Box sx={{ margin: '0px 12px 0px 0px' }}>
                                            <ProfitWidget
                                                text={'Section'}
                                                section={section}
                                                sectionOffer={section.offers[0]}
                                                sections={offer.sections}
                                                products={products}
                                                exchangeRates={exchangeRates}
                                                buyingRates={buyingRates}
                                                transportationMethod={transportationMethod}
                                                lockedExchangeRates={lockedExchangeRates}
                                                organization={org}
                                            />
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                            <Box>
                                {section.offers.map((sectionOffer, sectionOfferIndex) => (
                                    <Box key={sectionOffer.id} sx={{ border: `1px solid ${theme.palette.border1}`, borderRadius: '6px', marginTop: '16px', padding: '12px' }}>

                                        {(!status || status === 'draft') && !locked && section.offers.length > 1 && (
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: '0px 12px' }}>
                                                <ContextMenu
                                                    options={_.compact([
                                                        { 
                                                            key: 1, 
                                                            label: 'Reorder fields', 
                                                            icon: <FontAwesomeIcon icon={faShuffle} />,
                                                            onClick: () => setSortModal({ open: true, title: 'Sort Fields', items: sectionOffer.fields, path: `[${offerIndex}].sections[${sectionIndex}].offers[${sectionOfferIndex}].fields` })
                                                        },
                                                        (!showInformationBar.includes(sectionOffer.id) ? {
                                                            key: 2,
                                                            label: 'Add information bar',
                                                            icon: <FontAwesomeIcon icon={faInfoCircle} />,
                                                            onClick: () => {
                                                                setShowInformationBar(ib => ([...ib, sectionOffer.id]));
                                                            }

                                                        } : null),
                                                        { 
                                                            key: 3,
                                                            label: 'Delete offer',
                                                            icon: <FontAwesomeIcon icon={faTrash} />,
                                                            disabled: section.offers.length <= 1,
                                                            onClick: () => {
                                                                const newOffers = _.cloneDeep(offers);
                                                                const newSectionOffers = section.offers.filter(o => o.id !== sectionOffer.id);
                                                                _.set(newOffers, `[${offerIndex}].sections[${sectionIndex}].offers`, newSectionOffers);
                                                                setOffers(newOffers);
                                                            }
                                                        },
                                                    ])}
                                                />
                                            </Box>
                                        )}

                                        <SectionOfferHeader
                                            multipleOffers={section.offers.length > 1}
                                            sectionOffer={sectionOffer}        
                                            sections={offer.sections}
                                            offerIndex={offerIndex}
                                            sectionIndex={sectionIndex}
                                            sectionOfferIndex={sectionOfferIndex}
                                            setModal={setModal}
                                            products={products}    
                                            exchangeRates={exchangeRates}
                                            buyingRates={buyingRates}
                                            transportationMethod={transportationMethod}
                                            locked={locked}
                                            onAddSection={({ rate, section: updateSection }) => {
                                                const newOffers = _.cloneDeep(offers);
                                                const newSection = newOffers[offerIndex].sections[sectionIndex];
                                                updateSectionFromRateOrTariff({ rate, section: newSection, updateSection, products, sectionOfferIndex, organization: org });
                                                if (rate.type !== 'tariff') setBuyingRates(withRate(buyingRates, rate));
                                                setOffers(newOffers);
                                            }}
                                            lockedExchangeRates={lockedExchangeRates}
                                            organization={org}
                                        />
                                        
                                        {section.offers.length > 1 && showInformationBar.includes(sectionOffer.id) && (
                                            <Box sx={{ margin: '12px', display: 'flex', alignItems: 'center' }}>
                                                <Box sx={{ margin: '0px 12px 0px 0px' }}>
                                                    <SpecialInputField
                                                        icon={<FontAwesomeIcon icon={faClock} />}
                                                        emptyStateText={'Transit Time'}
                                                        title={'Set Transit Time'}
                                                        label={'Transit Time'}
                                                        path={`[${offerIndex}].sections[${sectionIndex}].offers[${sectionOfferIndex}].transitTime`}
                                                        inputType='transit-time'
                                                        options={getRatesForSection({ buyingRates, sectionOffer }).map(r => r.transportationMethod === 'sea' ? r.offer.transitTime * 24 : r.offer.transitTime)}
                                                        optionsDescription={'Add a transit time to this section. Cost rates transit times:'}
                                                        modal={modal}
                                                        setModal={setModal}
                                                        offers={offers}
                                                        setOffers={setOffers}
                                                        disabled={(!!status && status !== 'draft') || locked}
                                                    />
                                                </Box>

                                                <Box sx={{ margin: '0px 12px 0px 0px' }}>
                                                    <SpecialInputField
                                                        icon={<FontAwesomeIcon icon={faCalendar} />}
                                                        emptyStateText={'Validity'}
                                                        title={'Set Validity'}
                                                        label={'Validity'}
                                                        path={`[${offerIndex}].sections[${sectionIndex}].offers[${sectionOfferIndex}]`}
                                                        inputType='validity-range'
                                                        options={getRatesForSection({ buyingRates, sectionOffer }).map(r => r.offer.validUntil)}
                                                        optionsDescription={'Let your customers know from and until what date this section is valid. Cost rates validity:'}
                                                        modal={modal}
                                                        setModal={setModal}
                                                        offers={offers}
                                                        setOffers={setOffers}
                                                        disabled={(!!status && status !== 'draft') || locked}
                                                    />
                                                </Box>

                                                <Box sx={{ margin: '0px 12px 0px 0px' }}>
                                                    <SpecialInputField
                                                        icon={<FontAwesomeIcon icon={faRoute} />}
                                                        emptyStateText={'Route'}
                                                        title={'Set Route'}
                                                        label={'Route'}
                                                        path={`[${offerIndex}].sections[${sectionIndex}].offers[${sectionOfferIndex}].routes`}
                                                        inputType='routes'
                                                        modal={modal}
                                                        setModal={setModal}
                                                        offers={offers}
                                                        setOffers={setOffers}
                                                        disabled={(!!status && status !== 'draft') || locked}
                                                    />
                                                </Box>

                                                <Box sx={{ margin: '0px 12px 0px 0px' }}>
                                                    <SpecialInputField
                                                        icon={<FontAwesomeIcon icon={faBuilding} />}
                                                        emptyStateText={'Carrier'}
                                                        title={'Set Carrier'}
                                                        label={'Carrier'}
                                                        path={`[${offerIndex}].sections[${sectionIndex}].offers[${sectionOfferIndex}].carrier`}
                                                        inputType='carrier'
                                                        optionsDescription={'Add a carrier to the entire offer.'}
                                                        modal={modal}
                                                        setModal={setModal}
                                                        offers={offers}
                                                        setOffers={setOffers}
                                                        disabled={(!!status && status !== 'draft') || locked}
                                                    />
                                                </Box>
                                            </Box>
                                        )}

                                        <TableContainer>
                                            <Table sx={{ '& .MuiTableCell-root': { padding: '4px 8px' } }}>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ borderBottom: '0px', padding: '8px 4px 8px 8px', maxWidth: '280px' }}/>
                                                        <TableCell sx={{ borderBottom: '0px', padding: '8px 8px 8px 4px' }}/>
                                                        <TableCell sx={{ borderBottom: '0px', width: '50px', padding: '8px 8px' }} />
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {sectionOffer.fields.map((field, fieldIndex) => (
                                                        <OpportunityOfferFieldRow
                                                            key={field.id}
                                                            field={field}
                                                            organization={org}
                                                            products={products}
                                                            path={`[${offerIndex}].sections[${sectionIndex}].offers[${sectionOfferIndex}].fields[${fieldIndex}]`}
                                                            fieldIndex={fieldIndex}
                                                            locked={locked}
                                                            offers={offers}
                                                            offerIndex={offerIndex}
                                                            transportationMethod={transportationMethod}
                                                            setOffers={setOffers}
                                                            setModal={setModal}
                                                            disabled={(!!status && status !== 'draft') || locked}
                                                            buyingRates={buyingRates}
                                                            onShowRates={onShowRates}
                                                            onAddBuyingRate={onAddBuyingRate}
                                                            errors={errors.filter(e => e.id.startsWith(`${offerIndex}:${sectionIndex}:${sectionOfferIndex}:${fieldIndex}:`))}
                                                            sections={offer.sections}
                                                            exchangeRates={exchangeRates}
                                                            onDelete={() => {
                                                                const newSectionOffer = _.cloneDeep(sectionOffer);
                                                                newSectionOffer.fields = _.filter(newSectionOffer.fields, f => f.id !== field.id);

                                                                const newSection = _.cloneDeep(section);
                                                                newSection.offers = newSection.offers.map(s => s.id !== sectionOffer.id ? s : newSectionOffer);

                                                                const newOffer = _.cloneDeep(offer);
                                                                newOffer.sections = newOffer.sections.map(s => s.id !== section.id ? s : newSection);

                                                                setOffers(offers.map(o => o.id !== offer.id ? o : newOffer));
                                                            }}
                                                            suppliers={suppliers}
                                                            setBuyingRates={setBuyingRates}
                                                            onOpenDisplayCurrencySettings={() => setOfferDisplayCurrency({ offer, section, field })}
                                                            lockedExchangeRates={lockedExchangeRates}
                                                        />
                                                    ))}
                                                    <TableRow>
                                                        <TableCell colSpan={5} sx={{ padding: '4px 8px' }}>
                                                            {!locked && (
                                                                <AddFieldButton
                                                                    products={products}
                                                                    transportationMethod={transportationMethod}
                                                                    organization={org}
                                                                    onClick={() => {
                                                                        const newSectionOffer = _.cloneDeep(sectionOffer);
                                                                        newSectionOffer.fields = [...newSectionOffer.fields, { id: uuidv4(), type: 'per-unit', values: {} }];

                                                                        const newSection = _.cloneDeep(section);
                                                                        newSection.offers = newSection.offers.map(s => s.id !== sectionOffer.id ? s : newSectionOffer);

                                                                        const newOffer = _.cloneDeep(offer);
                                                                        newOffer.sections = newOffer.sections.map(s => s.id !== section.id ? s : newSection);

                                                                        setOffers(offers.map(o => o.id !== offer.id ? o : newOffer));
                                                                    }}
                                                                    onAddField={({ rate, field }) => {
                                                                        if (rate.type !== 'tariff') {
                                                                            setBuyingRates(_.uniqBy([rate, ...buyingRates], br => br.id));
                                                                        }

                                                                        const newSectionOffer = _.cloneDeep(sectionOffer);
                                                                        newSectionOffer.fields = [
                                                                            ...newSectionOffer.fields,
                                                                            newFieldFromRateOrTariff({ rate, section, field, products, organization: org, transportationMethod })
                                                                        ];

                                                                        const newSection = _.cloneDeep(section);
                                                                        newSection.offers = newSection.offers.map(s => s.id !== sectionOffer.id ? s : newSectionOffer);

                                                                        const newOffer = _.cloneDeep(offer);
                                                                        newOffer.sections = newOffer.sections.map(s => s.id !== section.id ? s : newSection);

                                                                        setOffers(offers.map(o => o.id !== offer.id ? o : newOffer));
                                                                    }}
                                                                />
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                ))}
                                {section.offers.length > 1 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                        {!locked && (
                                            <Button
                                                color='primary'
                                                variant='text'
                                                onClick={() => {
                                                    const newOffers = _.cloneDeep(offers);
                                                    const newSectionOffers = [
                                                        ...section.offers, 
                                                        { id: uuidv4(), fields: section.offers[0].fields.map(f => ({...f, id: uuidv4(), buyingRates: [], values: _.mapValues(v => ({...v, value: 0})) })) }
                                                    ];
                                                    _.set(newOffers, `[${offerIndex}].sections[${sectionIndex}].offers`, newSectionOffers);
                                                    setOffers(newOffers);
                                                    setShowInformationBar(b => _.uniq([...b, ...newSectionOffers.map(a => a.id)]))
                                                }}
                                            >
                                                + Offer
                                            </Button>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    ))}
                    {!locked && (
                        <AddSectionButton 
                            products={products}
                            onClick={() => {
                                const newOffer = _.cloneDeep(offer);
                                newOffer.sections = [...newOffer.sections, { id: uuidv4(), offers: [{ id: uuidv4(), fields: [] }] }];
                                setOffers(offers.map(o => o.id !== offer.id ? o : newOffer));
                            }}
                            onAddSection={({ rate, section}) => {
                                if (rate.type !== 'tariff') {
                                    setBuyingRates(withRate(buyingRates, rate));
                                }

                                const newOffer = _.cloneDeep(offer);
                                newOffer.sections = [...newOffer.sections, newSectionFromRateOrTariff({ rate, products, section, organization: org, transportationMethod })];
                                setOffers(offers.map(o => o.id !== offer.id ? o : newOffer));
                            }}
                        />
                    )}
                </Paper>
            ))}


            {locked && (
                <Box sx={{ ml: 3 }}>
                    <Alert severity='info'>
                        You are not allowed to edit this quote.
                    </Alert>
                </Box>
            )}

            {!locked && (
                <Box sx={{ ml: 3 }}>
                    <AddTemplate
                        products={products}
                        forms={org.forms}
                        transportationMethod={transportationMethod}
                        showAddGroupAnimation={showAddGroupAnimation}
                        routes={routes}
                        offers={offers}
                        setOffers={setOffers}
                        setBuyingRates={setBuyingRates}
                        contracts={contracts}
                        account={account}
                        organizationId={org.id}
                        disabled={!transportationMethod || _.isEmpty(products)}
                    />
                </Box>
            )}

            <OpportunityTitleEditModal
                modal={modal} 
                setModal={setModal} 
                offers={offers} 
                setOffers={setOffers} 
                seaPorts={seaPorts} 
                unlocode={unlocode}
                transportationMethod={transportationMethod} 
            />

            <OpportunityCargowiseCodesModal
                offer={cargowiseCodes ? cargowiseCodes.offer : null}
                onCancel={() => setCargowiseCodes(null)}
                onDone={offer => {
                    setOffers(offers.map(o => o.id === cargowiseCodes.offer.id ? offer : o));
                    setCargowiseCodes(null);
                }}
            />

            <TitleableReorders modal={sortModal} setModal={setSortModal} offers={offers} setOffers={setOffers} />

            <Drawer
                anchor={'right'}
                open={ratesMoreInfo.open}
                onClose={() => setRatesMoreInfo({...ratesMoreInfo, open: false})}
                hideBackdrop={true}
                variant='persistent'
                PaperProps= {{ 
                    sx: { background: theme.palette.panel1 }
                }}
            >
                <Box sx={{ width: minimizedSearch ? '637px' : '1157px', transition: 'width 0.2s' }}>
                    <Box sx={{ width: '600px', padding: '24px', height: '100vh', position: 'relative', paddingBottom: '24px' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <IconButton color='error' onClick={() => setRatesMoreInfo({...ratesMoreInfo, open: false})}>
                                <FontAwesomeIcon icon={faXmark} />
                            </IconButton>
                        </Box>
                        {(ratesMoreInfo.rates || []).map(r => (
                            <Box key={r.id} sx={{ paddingBottom: '24px' }}>
                                {r.source && (
                                    <Box sx={{ marginBottom: '30px' }}>
                                        <RateCard
                                            organization={org}
                                            rate={r}
                                            exchangeRates={exchangeRates}
                                            transitDatesIndex={0}
                                            standalone={true}
                                        />
                                    </Box>
                                )}
                                <RatesDetails
                                    organization={org}
                                    rates={[r]}
                                    emphasize={ratesMoreInfo.emphasize}
                                    onStartDragging={() => setRatesMoreInfo({...ratesMoreInfo, open: false})}
                                    onEndDragging={() => setRatesMoreInfo({...ratesMoreInfo, open: true})}
                                    transportationMethod={transportationMethod}
                                />
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Drawer>
            {addBuyingRateDialog && (
                <AddBuyingRateDialog 
                    {...addBuyingRateDialog} 
                    products={products}
                    offers={offers}
                    setOffers={setOffers}
                    onClose={() => {
                        const _resolve = addBuyingRateDialog.resolve;
                        setAddBuyingRateDialog(null);
                        _resolve();
                    }}
                    buyingRates={buyingRates}
                    onAddBuyingRate={async (v) => {
                        const _resolve = addBuyingRateDialog.resolve;
                        setAddBuyingRateDialog(null);
                        await onAddBuyingRate(v);
                        _resolve();
                    }}
                />
            )}
            {!!offerDisplayCurrency && (
                <DisplayCurrencySettingsDialog
                    open={!!offerDisplayCurrency}
                    onClose={() => setOfferDisplayCurrency(null)}
                    offer={offerDisplayCurrency.offer}
                    section={offerDisplayCurrency.section}
                    field={offerDisplayCurrency.field}
                    setOffer={offer => {
                        const newOffers = _.cloneDeep(offers);
                        const newOffersUpdates = newOffers.map(o => o.id === offer.id ? offer : o);
                        setOffers(newOffersUpdates);
                    }}
                    organization={org}
                    exchangeRates={exchangeRates}
                    lockedExchangeRates={lockedExchangeRates}
                />
            )}

            <Box sx={{ background: 'transparent', padding: '6px 24px', mb: 3, mt: 3, borderBottom: '1px solid rgba(59, 130, 246, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant='body1' sx={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#334155' }}>
                    Terms & Conditions
                </Typography>
            </Box>
            <Paper sx={{ margin: '0px 24px 24px 24px', padding: '12px', border: `1px solid ${theme.palette.border1}` }} elevation={0}>
                {!_.isEmpty(terms) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant='body2' sx={{ padding: '0px 24px' }}>
                            The following {pluralize('term', terms.length)} and {pluralize('condition', terms.length)} will be added to the bottom of the email: {terms.map((t, i) => (
                                <span key={t.name}>
                                    <Tooltip title={t.value}>
                                        <span style={{ textDecoration: 'underline' }}>{t.name ? _.startCase(t.name) : 'Custom terms'}</span>
                                    </Tooltip>
                                    {i < terms.length - 2 ? ',' : i === terms.length - 1 ? '' : ', and '}
                                </span>
                            ))}
                        </Typography>
                        <Button
                            variant='contained'
                            color='primary'
                            onClick={() => setTermsOpen(true)}
                        >
                            Edit
                        </Button>
                    </Box>
                )}
                {(_.isNull(terms) || _.isUndefined(terms)) && (
                    <Alert severity='info' sx={{ margin: '0px 8px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <Typography variant='caption' sx={{ fontSize: '11px' }}>
                                Using default terms & conditions{transportationMethod ? ` for ${transportationMethod}` : ''}.
                            </Typography>
                            <Button
                                variant='contained'
                                color='primary'
                                size='small'
                                onClick={() => setTermsOpen(true)}
                                sx={{ textTransform: 'none', fontSize: '11px' }}
                            >
                                Edit
                            </Button>
                        </Box>
                    </Alert>
                )}
                {_.isArray(terms) && _.isEmpty(terms) && (
                    <Alert severity='info' sx={{ margin: '0px 8px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <Typography variant='caption' sx={{ fontSize: '11px' }}>
                                No terms & conditions selected.
                            </Typography>
                            <Button
                                sx={{ ml: 2 }}
                                variant='contained'
                                color='primary'
                                size='small'
                                onClick={() => setTermsOpen(true)}
                                sx={{ textTransform: 'none', fontSize: '11px' }}
                            >
                                Edit
                            </Button>
                        </Box>
                    </Alert>
                )}
            </Paper>
            {termsOpen && (
                <TermsDialog
                    open={true}
                    onClose={() => setTermsOpen(false)}
                    onSave={(terms) => setTerms(terms)}
                    terms={terms}
                    transportationMethod={transportationMethod}
                    organization={org}
                />
            )}
        </Box>
    )
}

export default OpportunityOneOffBuilder;

function findPathForField({ offers, fieldId }) {
    let offerIndex, sectionIndex, sectionOfferIndex, fieldIndex;

    offers.forEach((o, oi) => o.sections.forEach((s, si) => s.offers.forEach((so, soi) => so.fields.forEach((f, fi) => {
        if (f.id === fieldId) {
            offerIndex = oi;
            sectionIndex = si;
            sectionOfferIndex = soi;
            fieldIndex = fi;
        }
    }))));

    return {
        path: `[${offerIndex}].sections[${sectionIndex}].offers[${sectionOfferIndex}].fields[${fieldIndex}]`,
        sectionPath: `[${offerIndex}].sections[${sectionIndex}]`,
        offerIndex,
        sectionIndex,
        sectionOfferIndex,
        fieldIndex,
    }
}

function getRatesForSection({ buyingRates, sectionOffer }) {
    const fieldBuyingRates = _.flatten(sectionOffer.fields.map(f => f.buyingRates || [])).map(r => r.rateId);
    return (buyingRates || []).filter(br => fieldBuyingRates.includes(br.id));
}

function getRatesForOffer({ buyingRates, offer }) {
    const allBuyingRates = _.uniq(_.compact(_.flatten(_.flatten(_.flatten(offer.sections.map(s => s.offers.map(so => so.fields.map(f => (f.buyingRates || []).map(br => br.rateId)))))))));
    const ret = _.compact(allBuyingRates.map(br => buyingRates.find(r => r.id === br)));
    return ret;
}

function AddSectionButton(props) {
    const { onClick, products, onAddSection } = props;
    const [ dropOver, setDropOver ] = useState(null);

    const first = useRef();
    const setDropOverRef = useRef();
    setDropOverRef.current = setDropOver;
    const productsRef = useRef();
    productsRef.current = products;
    const onAddSectionRef = useRef();
    onAddSectionRef.current = onAddSection;

    const [ , drop ] = useDrop(() => ({ 
        accept: ['RateCardSingleSection'],
        hover: (item, monitor) => {
            if (first.current) return;
            first.current = true;
            setDropOverRef.current(monitor.canDrop(item, monitor) ? 'candrop' + (item.rate.type === 'tariff' ? '-tariff' : '') : 'cantdrop' + (item.rate.type === 'tariff' ? '-tariff' : ''));
            function isOver() {
                if (monitor.isOver()) {
                    setTimeout(isOver, 100);
                } else {
                    first.current = false;
                    setDropOverRef.current(null);
                }
            }
            setTimeout(isOver, 100);
        },
        drop: (item, monitor) => {
            onAddSectionRef.current({rate: item.rate, section: item.section });
        },
        canDrop: (item, monitor) => {
            return _.some(productsRef.current, p => {
                const _type = item.rate.product.type;
                const _type2 = p.type;
                if (_type !== _type2) return false;
                if ((item.rate.product.dangerous || false) !== (p.dangerous || false)) return false;
                return true;
            });
        },
    }));

    return (
        <Button 
            variant='outlined' 
            color='primary' 
            fullWidth 
            sx={{ padding: '24px', borderStyle: 'dashed', marginTop: '12px', textTransform: 'none', flexDirection: 'column', borderRadius: '6px' }}
            onClick={onClick}
            ref={drop}
        >
            <Box>
                + SECTION
            </Box>
            <Typography variant='caption' component={Box}>
                Drop your rate or tariff here to add a section with its fields.
            </Typography>
            {dropOver === 'candrop' && (
                <Box sx={{ position: 'absolute', background: grey[800], opacity: 0.8, top: '0px', left: '0px', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', textAlign: 'center' }}>
                    Drop your rate here to add a section with its fields.
                </Box>
            )}
            {dropOver === 'candrop-tariff' && (
                <Box sx={{ position: 'absolute', background: grey[800], opacity: 0.8, top: '0px', left: '0px', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', textAlign: 'center' }}>
                    Drop your tariff here to add section with its fields.
                </Box>
            )}
            {dropOver === 'cantdrop' && (
                <Box sx={{ position: 'absolute', background: red[500], opacity: 0.8, top: '0px', left: '0px', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', textAlign: 'center' }}>
                    Rate is for a product not included in the opportunity.
                </Box>
            )}
            {dropOver === 'cantdrop-tariff' && (
                <Box sx={{ position: 'absolute', background: red[500], opacity: 0.8, top: '0px', left: '0px', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', textAlign: 'center' }}>
                    Tariff is for a product not included in the opportunity.
                </Box>
            )}
        </Button>
    )
}

function AddFieldButton(props) {
    const { onClick, products, onAddField, transportationMethod, organization } = props;
    const [ dropOver, setDropOver ] = useState(null);

    const first = useRef();
    const setDropOverRef = useRef();
    setDropOverRef.current = setDropOver;
    const productsRef = useRef();
    productsRef.current = products;
    const onAddFieldRef = useRef();
    onAddFieldRef.current = onAddField;

    const [ , drop ] = useDrop(() => ({ 
        accept: ['RateCardSingleRate'],
        hover: (item, monitor) => {
            if (first.current) return;
            first.current = true;
            setDropOverRef.current(monitor.canDrop(item, monitor) ? 'candrop' + (item.rate.type === 'tariff' ? '-tariff' : '') : 'cantdrop' + (item.rate.type === 'tariff' ? '-tariff' : ''));
            function isOver() {
                if (monitor.isOver()) {
                    setTimeout(isOver, 100);
                } else {
                    first.current = false;
                    setDropOverRef.current(null);
                }
            }
            setTimeout(isOver, 100);
        },
        drop: (item, monitor) => {
            onAddFieldRef.current({rate: item.rate, field: item.field });
        },
        canDrop: (item, monitor) => {
            return !_.isEmpty(findMatchingProducts({ 
                product: item.rate.product, 
                products: productsRef.current
            }));
        },
    }));

    return (
        <Button
            ref={drop}
            variant='outlined' 
            color='primary' 
            fullWidth 
            sx={{ padding: '24px', borderStyle: 'dashed', textTransform: 'none', flexDirection: 'column', borderRadius: '6px' }}
            onClick={onClick}
        >
            + FIELD
            <Typography variant='caption' component={Box}>
                Drop your rate or tariff here to add a new field
            </Typography>
            {dropOver === 'candrop' && (
                <Box sx={{ position: 'absolute', background: grey[800], opacity: 0.8, top: '0px', left: '0px', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', textAlign: 'center' }}>
                    Drop your rate here to add a new field
                </Box>
            )}
            {dropOver === 'candrop-tariff' && (
                <Box sx={{ position: 'absolute', background: grey[800], opacity: 0.8, top: '0px', left: '0px', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', textAlign: 'center' }}>
                    Drop your tariff here to add a new field
                </Box>
            )}
            {dropOver === 'cantdrop' && (
                <Box sx={{ position: 'absolute', background: red[500], opacity: 0.8, top: '0px', left: '0px', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', textAlign: 'center' }}>
                    Rate is for a product not included in the opportunity.
                </Box>
            )}
            {dropOver === 'cantdrop-tariff' && (
                <Box sx={{ position: 'absolute', background: red[500], opacity: 0.8, top: '0px', left: '0px', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', textAlign: 'center' }}>
                    Tariff is for a product not included in the opportunity.
                </Box>
            )}
        </Button>
    );
}

function newSectionFromRateOrTariff({ rate, products, section, organization, transportationMethod }) {
    // Convert rate from KG to LBS if needed before using its values
    const convertedRate = convertRateKgToLbsIfNeeded({ rate, opportunityProducts: products });
    // Use the converted section from the converted rate if available, otherwise use the original section
    const convertedSection = convertedRate.offer.sections.find(s => s.id === section.id) || section;
    return {
        title: convertedSection.title || '',
        description: convertedSection.description || '',
        id: uuidv4(), 
        offers: [{ 
            id: uuidv4(), 
            fields: convertedSection.offers[0].fields.map(field => {
                const originalField = section.offers[0].fields.find(f => f.id === field.id) || field;
                return newFieldFromRateOrTariff({ rate: convertedRate, section: convertedSection, products, field: originalField, organization, transportationMethod });
            })
        }] 
    };
}

function newFieldFromRateOrTariff({ rate, section, products, field, organization, transportationMethod }) {
    // Convert rate from KG to LBS if needed before using its values
    const convertedRate = convertRateKgToLbsIfNeeded({ rate, opportunityProducts: products });
    const matchingProducts = findMatchingProducts({ product: convertedRate.product, products });
    
    // Find the converted field value from the converted rate
    const convertedField = (() => {
        for (const s of convertedRate.offer.sections) {
            for (const o of s.offers) {
                const f = o.fields.find(f => f.id === field.id);
                if (f) return f;
            }
        }
        return field;
    })();

    let type = field.type;

    // Check if field type is enabled, if not change to 'per-unit'
    const fieldTypeConfig = FieldTypes[transportationMethod]?.find(t => t.value === type);
    if (fieldTypeConfig?.enabled && !fieldTypeConfig.enabled({ organization })) {
        type = 'per-unit';
    }
    
    return {
        ...field,
        type,
        id: uuidv4(), 
        buyingRates: convertedRate.type === 'tariff' ? [] : 
            matchingProducts.map(p => ({ 
                rateId: convertedRate.id, 
                fieldId: field.id, 
                opportunityProductId: p.id 
            })),
        values: _.fromPairs([...products, {id: 'flat'}, {id: 'custom'}].map(p => {
            if (convertedRate.type !== 'tariff') {
                return [p.id, { currency: _.first(_.values(convertedField.values))?.currency || section.defaultCurrency, value: 0 }];
            }
            if (p.type === convertedRate.product.type && field.type !== 'flat') {
                return [p.id, convertedField.values[convertedRate.product.id]];
            }
            if (p.type === 'flat' && field.type === 'flat') {
                return [p.id, convertedField.values.flat];
            }
            return [p.id, { currency: organization.defaults?.currency }];
        }))
    }
}

function updateSectionFromRateOrTariff({ rate, updateSection, section, products, sectionOfferIndex = -1, organization }) {
    // Convert rate from KG to LBS if needed before using its values
    const convertedRate = convertRateKgToLbsIfNeeded({ rate, opportunityProducts: products });

    console.log('updateSectionFromRateOrTariff', convertedRate, updateSection, section, products, sectionOfferIndex);

    section.offers.forEach((sectionOffer, _sectionOfferIndex) => {
        if (sectionOfferIndex !== -1 && sectionOfferIndex !== _sectionOfferIndex) return;
        updateSection.offers[0].fields.forEach(updateField => {
            const existingField = sectionOffer.fields.find(f => f.title === updateField.title);
            if (existingField) {
                if (convertedRate.type !== 'tariff') {
                    const matchingProducts = findMatchingProducts({ 
                        product: convertedRate.product, 
                        products
                    });
                    matchingProducts.forEach(product => {
                        existingField.buyingRates = _.uniqWith([...existingField.buyingRates, { rateId: rate.id, fieldId: updateField.id, opportunityProductId: product.id }], _.isEqual);
                    });
                } else {
                    // Find the converted field value from the converted rate
                    const convertedField = (() => {
                        for (const s of convertedRate.offer.sections) {
                            for (const o of s.offers) {
                                const f = o.fields.find(f => f.id === updateField.id);
                                if (f) return f;
                            }
                        }
                        return updateField;
                    })();
                    
                    products.forEach(product => {
                        if (product.type !== convertedRate.product.type) return;
                        existingField.values[product.id] = convertedField.values[convertedRate.product.id];
                    });
                }
            } else {
                const newField = newFieldFromRateOrTariff({ rate: convertedRate, section, field: updateField, products, organization, transportationMethod: rate.transportationMethod });
                sectionOffer.fields.push(newField);
            }
        });
    });
}

function AddBuyingRateDialog(props) {
    const { open, onClose, rate, rateFieldId, opportunityFieldId, offers, setOffers, products, buyingRates, onAddBuyingRate } = props;

    const opportunityField =  useMemo(() => {
        const { path } = findPathForField({ offers, fieldId: opportunityFieldId });
        return _.get(offers, path);
    }, [offers, opportunityFieldId]);

    const [ rateField, rateSection ] = useMemo(() => {
        const { path, sectionPath } = findPathForField({ offers: [rate.offer], fieldId: rateFieldId });
        return [ _.get([rate.offer], path), _.get([rate.offer], sectionPath) ];
    }, [rate, rateFieldId]);

    const _products = findMatchingProducts({ products, product: rate.product });

    const productsWithField = useMemo(() => {
        return _.compact(opportunityField?.buyingRates?.map(br => {
            const _rate = _.find(buyingRates, r => r.id === br.rateId);
            const { path, sectionPath } = findPathForField({ offers: [_rate.offer], fieldId: br.fieldId });
            const field = _.get([_rate.offer], path);
            const section = _.get([_rate.offer], sectionPath);
            if (!field) return null;
            if (field.title === rateField.title && section.title === rateSection.title) return products.find(p => p.id === br.opportunityProductId);
            return null;
        }));
    }, [opportunityField.buyingRates, buyingRates, products, rateFieldId, rateField]);

    const linkedToProducts = useMemo(() => {
        return !_.isEmpty(_.intersectionBy(_products, productsWithField));
    }, [_products, productsWithField]);

    const linkedToOtherProducts = useMemo(() => {
        return !linkedToProducts && !_.isEmpty(productsWithField);
    }, [linkedToProducts, productsWithField]);

    function onAdd() {
        onAddBuyingRate({ rate, rateFieldId, opportunityFieldId, force: true });
    }

    return (
        <Dialog open={open} onClose={() => {}} sx={{ zIndex: 10000000 }}>
            <DialogTitle>Add Buying Rate</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    {linkedToProducts && 'Field is priced "By Shipment", and is already linked to similar products in the opportunity.'}
                    {linkedToOtherProducts && 'Field is priced "By Shipment", and is already linked to other product types in the opportunity. Are you sure you want to add it again?'}
                    {!linkedToProducts && !linkedToOtherProducts && `Note, you are adding a field ${rateField.title} that is priced "By Shipment" to a quote line that is priced otherwise.`}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                {!linkedToProducts && (
                    <Button onClick={onAdd}>Add</Button>
                )}
            </DialogActions>
        </Dialog>
    )
}

function DisplayCurrencySettingsDialog(props) {
    const { open, onClose, offer, section, field, setOffer, organization, exchangeRates, lockedExchangeRates } = props;
    const [ innerItem, setInnerItem ] = useState(field || section || offer);
    const bulkUpdateRef = useRef(null);

    useEffect(() => {
        setInnerItem(field || section || offer);
    }, [field, section, offer]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
            <DialogTitle>Set Display Currency</DialogTitle>
            <DialogContent sx={{ padding: 2}}>
                {field && (
                    <DisplayCurrencySettingsField
                        field={innerItem}
                        setField={setInnerItem}
                        organization={organization}
                        exchangeRates={exchangeRates}
                        lockedExchangeRates={lockedExchangeRates}
                    />
                )}
                {!field && section && (
                    <DisplayCurrencySettingsSection
                        section={innerItem}
                        setSection={setInnerItem}
                        organization={organization}
                        exchangeRates={exchangeRates}
                        lockedExchangeRates={lockedExchangeRates}
                    />
                )}
                {!field && !section && (
                    <DisplayCurrencySettingsOffer
                        offer={innerItem}
                        setOffer={setInnerItem}
                        organization={organization}
                        exchangeRates={exchangeRates}
                        lockedExchangeRates={lockedExchangeRates}
                    />
                )}
                <BulkUpdateSettings field={field} section={section} offer={offer} ref={bulkUpdateRef} />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant='contained' onClick={() => {
                    const bulkUpdate = bulkUpdateRef.current.get();
                    if (field) {
                        const o = _.cloneDeep(offer);
                        o.sections?.forEach(_section => _section.offers?.forEach(sectionOffer => sectionOffer.fields.forEach(field => {
                            if (field.id === innerItem.id) {
                                field.displayCurrency = innerItem.displayCurrency;
                                field.displayCurrencySource = innerItem.displayCurrencySource;
                            }
                        })));
                        setOffer(o);
                    } else if (section) {
                        const o = _.cloneDeep(offer);
                        o.sections.forEach(section => {
                            if (section.id === innerItem.id) {
                                section.displayCurrency = innerItem.displayCurrency;
                                section.displayCurrencySource = innerItem.displayCurrencySource;
                                if (bulkUpdate) {
                                    section.offers.forEach(offer => {
                                        offer.fields.forEach(field => {
                                            field.displayCurrency = innerItem.displayCurrency;
                                            field.displayCurrencySource = innerItem.displayCurrencySource;
                                        });
                                    });
                                }
                            }
                        });
                        setOffer(o);
                    } else {
                        if (bulkUpdate) {
                            innerItem.sections.forEach(section => {
                                section.displayCurrency = innerItem.displayCurrency;
                                section.displayCurrencySource = innerItem.displayCurrencySource;
                                section.offers.forEach(offer => {
                                    offer.fields.forEach(field => {
                                        field.displayCurrency = innerItem.displayCurrency;
                                        field.displayCurrencySource = innerItem.displayCurrencySource;
                                    });
                                });
                            });
                        }
                        setOffer(innerItem);
                    }
                    onClose();
                }} sx={{ fontFamily: 'Poppins, sans-serif' }}>Save</Button>
            </DialogActions>
        </Dialog>
    )
}
